'use strict';

const libPictProvider = require('pict-provider');

const libCatalog = require('../data-import/Import-Entity-Catalog.js');
const libSchema = require('../data-import/DataImport-Schema.js');
const libContextCatalog = require('../data-import/Import-Context-Catalog.js');
const libXlsxParser = require('../data-import/DataImport-Xlsx-Parser.js');

// The wizard view's constant mount point (the host view's template provides this element) + a per-entity
// wizard view hash (each target entity gets its own wizard + upload/mapping session so switching targets
// never bleeds one entity's file/mapping into another).
const WIZARD_MOUNT = '#DataImport-Mount';
const WIZARD_HASH_PREFIX = 'HeadlightImport-';

// The GUID-marshal prefix the import stamps on every record. Combined with the natural key the user maps
// to the GUID template, this makes a re-import idempotent (same key -> same Meadow GUID -> upsert in place).
const IMPORT_GUID_PREFIX = 'HLIMPORT';

const _DEFAULT_CONFIGURATION =
{
	ProviderIdentifier: 'DataImportManager',

	AutoInitialize: true,
	AutoInitializeOrdinal: 90,
};

/**
 * DataImportManager — the orchestrator behind the Import Data screen.
 *
 * It owns the curated target-entity list and the two host-supplied seams that let pict-section-dataimport
 * run entirely through the Headlight rest client (the config app has no `pict.EntityProvider`, which the
 * module's stock 'meadow' schema source / 'entityprovider' push both require):
 *   - SchemaSource: a function that fetches `/1.0/<Entity>/Schema` via HeadlightRestClient and maps it.
 *   - PushMode: a push target that runs meadow-integration's Integration Adapter against HeadlightRestClient
 *     (GUID marshaling + idempotent upsert), with PerformDeletes off.
 *
 * All host-view render state lives at `AppData.DataImportUI` (the wizard module owns `AppData.DataImport`).
 */
class DataImportManagerProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		const tmpOptions = Object.assign({}, _DEFAULT_CONFIGURATION, pOptions);
		super(pFable, tmpOptions, pServiceHash);

		/** @type {any} */
		this.pict;
		this._meadowEngine = null;
		this._initializedWizards = {};
	}

	/** The shared Headlight rest client (drives every schema read + record upsert). */
	get restClient()
	{
		return (this.fable && this.fable.HeadlightRestClient)
			|| (this.pict && this.pict.fable && this.pict.fable.HeadlightRestClient)
			|| (this.pict && this.pict.HeadlightRestClient)
			|| null;
	}

	/** The embedded pict-section-dataimport provider (createImportWizard lives here). */
	get dataImportProvider()
	{
		return this.pict.providers['Pict-Section-DataImport'];
	}

	/** The app's shared modal (success / error toasts). */
	get modal()
	{
		return this.pict.views['Pict-Section-Modal'];
	}

	/** Lazily require the browser-safe meadow-integration engine (the Integration Adapter). */
	_engine()
	{
		if (!this._meadowEngine)
		{
			this._meadowEngine = require('meadow-integration/source/Meadow-Integration-Engine.js');
		}
		return this._meadowEngine;
	}

	/** Ensure the host-view state object exists at AppData.DataImportUI. */
	_ensureState()
	{
		if (!this.pict.AppData.DataImportUI)
		{
			this.pict.AppData.DataImportUI =
			{
				SelectedEntity: '',
				ActiveWizardHash: '',
				LastResult: null,
				LastError: '',
				// Derived each prepare():
				Entities: [],
				SelectedSlot: [],
				IntroSlot: [],
				ResultSlot: [],
				ErrorSlot: [],
				MissingSlot: [],
			};
		}
		return this.pict.AppData.DataImportUI;
	}

	/**
	 * Recompute the derived render slots from the core state. Called by the host view's onBeforeRender.
	 */
	prepare()
	{
		const tmpState = this._ensureState();
		const tmpSelected = tmpState.SelectedEntity;

		tmpState.Entities = libCatalog.IMPORT_ENTITIES.map((pEntity) => Object.assign({}, pEntity,
			{ Selected: (pEntity.Entity === tmpSelected) }));

		const tmpEntity = tmpSelected ? libCatalog.findEntity(tmpSelected) : null;
		tmpState.SelectedSlot = tmpEntity
			? [ { Label: tmpEntity.Label, Icon: tmpEntity.Icon, Description: tmpEntity.Description } ]
			: [];
		tmpState.IntroSlot = tmpEntity ? [] : [ {} ];

		tmpState.ResultSlot = tmpState.LastResult
			? [ {
				Message: tmpState.LastResult.Message,
				ViewRoute: `#/PSRS/${tmpState.LastResult.Entity}/List`,
				ViewLabel: 'View records',
			} ]
			: [];
		tmpState.ErrorSlot = tmpState.LastError ? [ { Message: tmpState.LastError } ] : [];

		tmpState.MissingSlot = this.dataImportProvider ? [] : [ {} ];
		return tmpState;
	}

	/**
	 * The user picked a target entity (or cleared the picker). (Re)build + mount that entity's wizard.
	 * @param {string} pEntityName
	 */
	selectEntity(pEntityName)
	{
		const tmpState = this._ensureState();
		const tmpEntity = pEntityName ? libCatalog.findEntity(pEntityName) : null;

		tmpState.SelectedEntity = tmpEntity ? tmpEntity.Entity : '';
		tmpState.LastResult = null;
		tmpState.LastError = '';

		if (tmpEntity)
		{
			this._buildWizard(tmpEntity.Entity);
		}
		else
		{
			tmpState.ActiveWizardHash = '';
		}
		this._render();
	}

	/** (Re)configure the per-entity import wizard with the Headlight schema + push seams. */
	/** The host xlsx parser (window.XLSX-aware), cached — shared by every wizard + the Contract-package import. */
	_xlsxParser()
	{
		if (!this._cachedXlsxParser) { this._cachedXlsxParser = new libXlsxParser(this.pict, {}); }
		return this._cachedXlsxParser;
	}

	_buildWizard(pEntityName)
	{
		const tmpProvider = this.dataImportProvider;
		if (!tmpProvider)
		{
			this.log.error('DataImport: the Pict-Section-DataImport provider is not registered.');
			return;
		}
		const tmpHash = `${WIZARD_HASH_PREFIX}${pEntityName}`;
		tmpProvider.createImportWizard(tmpHash,
			{
				DestinationAddress: WIZARD_MOUNT,
				RenderMode: 'wizard',
				URLPrefix: '/1.0/',
				GUIDPrefix: IMPORT_GUID_PREFIX,
				EntityGUIDPrefix: `E-${pEntityName}`,
				AllowGUIDTruncation: true,
				AllowedFileTypes: [ 'csv', 'tsv', 'xlsx', 'fixedwidth' ],
				AllowDownload: true,
				// Excel: the stock module parser require()s xlsx, which the Headlight build --ignores (SheetJS is
				// loaded as window.XLSX). Override with the host parser that prefers the global. See the parser.
				Parsers: { xlsx: this._xlsxParser() },
				// Context-aware GUID strategy: stable, length-safe, interconnected GUIDs (UI_<context>_<own>).
				// The composer + compiler live in meadow-integration; this app supplies only the catalog, and
				// the user tunes per-entity own-key + parent links (with a live preview) in the mapping step.
				GUIDStrategy: true,
				GUIDStrategyPrefix: 'UI',
				ContextEntityCatalog: libContextCatalog,
				// Host-supplied seams (no pict.EntityProvider in this app):
				SchemaSource: this._schemaSourceFor(pEntityName),
				MeadowEntities: [ pEntityName ],
				PushMode: this._buildPushTarget(),
				OnPushComplete: (pResult) => this._onPushComplete(pEntityName, pResult),
				OnError: (pError) => this._onError(pError),
			});
		this.pict.AppData.DataImportUI.ActiveWizardHash = tmpHash;
	}

	/** (Re)render the active wizard into the host page's mount div (called from the host view's onAfterRender). */
	mountWizard()
	{
		const tmpState = this._ensureState();
		const tmpHash = tmpState.ActiveWizardHash;
		if (!tmpHash)
		{
			return;
		}
		if (this.pict.ContentAssignment.getElement(WIZARD_MOUNT).length === 0)
		{
			return;
		}
		const tmpView = this.pict.views[tmpHash];
		if (!tmpView)
		{
			return;
		}
		// A view added after the boot cycle isn't initialized automatically — initialize once before render.
		if (!this._initializedWizards[tmpHash] && (typeof tmpView.initialize === 'function'))
		{
			tmpView.initialize();
			this._initializedWizards[tmpHash] = true;
		}
		// pict-section-dataimport >= 1.0.1 rebuilds its accordion when a host re-render has wiped the mount,
		// so a plain render() is enough here — no need to reach into the wizard's internal `_wired` latch.
		tmpView.render();
	}

	/**
	 * A SchemaSource function: fetch `/1.0/<Entity>/Schema` via HeadlightRestClient, map it to the
	 * dataimport target-schema shape. (The stock 'meadow' source needs pict.EntityProvider.)
	 * @param {string} pEntityName
	 * @returns {(pContext:Record<string,any>) => Promise<{Entities:Array<any>, Order:Array<string>}>}
	 */
	_schemaSourceFor(pEntityName)
	{
		return () => new Promise((resolve, reject) =>
		{
			const tmpRestClient = this.restClient;
			if (!tmpRestClient)
			{
				return reject(new Error('HeadlightRestClient is not available for the schema read.'));
			}
			// getJSON prepends the /1.0/ base, so pass the entity-relative path.
			tmpRestClient.getJSON(`${pEntityName}/Schema`, (pError, pBody) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				return resolve(libSchema.mapEntitySchema(pEntityName, pBody));
			});
		});
	}

	/**
	 * A PushTarget: land the generated comprehension via meadow-integration's Integration Adapter running
	 * against HeadlightRestClient (the same engine the stock 'entityprovider' push uses, minus its
	 * dependency on pict.EntityProvider). One fresh adapter per entity, pushed in order.
	 * @returns {{push:(pComprehension:Record<string,any>, pContext:Record<string,any>) => Promise<any>}}
	 */
	_buildPushTarget()
	{
		return { push: (pComprehension, pContext) => this._pushViaHeadlight(pComprehension, pContext) };
	}

	/**
	 * @param {Record<string,any>} pComprehension @param {Record<string,any>} pContext
	 * @returns {Promise<{Success:boolean, EntitiesPushed:Array<string>, Message:string}>}
	 */
	_pushViaHeadlight(pComprehension, pContext)
	{
		const tmpRestClient = this.restClient;
		if (!tmpRestClient)
		{
			return Promise.reject(new Error('HeadlightRestClient is not available for the import push.'));
		}
		const tmpEngine = this._engine();
		const tmpContext = pContext || {};
		const tmpOrder = (Array.isArray(tmpContext.Order) && (tmpContext.Order.length > 0))
			? tmpContext.Order
			: Object.keys(pComprehension || {});

		const tmpPushed = [];
		let tmpTotal = 0;
		tmpOrder.forEach((pEntity) => { tmpTotal += Object.keys((pComprehension && pComprehension[pEntity]) || {}).length; });
		let tmpDone = 0;

		// Sequential reduce over the ordered entities (referenced entities first; v1 is single-entity).
		return tmpOrder.reduce((pPromise, pEntity) => pPromise.then(() =>
		{
			const tmpRecords = (pComprehension && pComprehension[pEntity]) || {};
			const tmpGUIDs = Object.keys(tmpRecords);
			if (tmpGUIDs.length === 0)
			{
				return null;
			}

			const tmpAdapterOptions = Object.assign({}, tmpEngine.MeadowIntegrationAdapter.default_configuration,
				{
					Entity: pEntity,
					// Honor an EXPLICITLY-EMPTY prefix: the GUID-strategy path composes the entire GUID itself
					// (e.g. `UI_P01278`) and sets both marshal prefixes to '' so the adapter stores it verbatim.
					// A plain `|| default` turns that '' back into the import default and double-marshals the GUID
					// (`HLIMPORT-E-Project-UI_P01278`), which breaks cross-session FK matching. Use the value when
					// it's a string (incl. '') and fall back only when it's truly unset.
					EntityGUIDMarshalPrefix: (typeof tmpContext.EntityGUIDPrefix === 'string') ? tmpContext.EntityGUIDPrefix : `E-${pEntity}`,
					AdapterSetGUIDMarshalPrefix: (typeof tmpContext.GUIDPrefix === 'string') ? tmpContext.GUIDPrefix : IMPORT_GUID_PREFIX,
					SimpleMarshal: true,
					ForceMarshal: true,
					PerformDeletes: false,
					AllowGUIDTruncation: !!tmpContext.AllowGUIDTruncation,
					// Real per-entity GUID column widths (from the wizard's resolved schema). The strategy
					// composer already sized each GUID to fit its column; without these the adapter's
					// auto-detect (which reads meadow `Columns`, not our JSON-schema `properties`) falls back to
					// a small default and re-truncates a composed GUID — clipping the entity's own segment.
					GUIDColumnSizes: tmpContext.GUIDColumnSizes || {},
				});
			const tmpAdapter = new tmpEngine.MeadowIntegrationAdapter(this.pict, tmpAdapterOptions, `DataImport-${pEntity}`);
			tmpAdapter.setRestClient(tmpRestClient);
			tmpGUIDs.forEach((pGUID) => tmpAdapter.addSourceRecord(tmpRecords[pGUID]));

			return new Promise((resolve, reject) =>
			{
				tmpAdapter.integrateRecords((pError) =>
				{
					if (pError)
					{
						return reject(pError);
					}
					tmpPushed.push(pEntity);
					tmpDone += tmpGUIDs.length;
					if (typeof tmpContext.onProgress === 'function')
					{
						tmpContext.onProgress(tmpDone, tmpTotal);
					}
					return resolve(null);
				});
			});
		}), Promise.resolve()).then(() =>
		{
			return {
				Success: true,
				EntitiesPushed: tmpPushed,
				Message: `Imported ${tmpDone} ${(tmpDone === 1) ? 'record' : 'records'} into ${tmpPushed.join(', ') || 'the selected entity'}.`,
			};
		});
	}

	/** @param {string} pEntityName @param {Record<string,any>} pResult */
	_onPushComplete(pEntityName, pResult)
	{
		const tmpState = this._ensureState();
		const tmpResult = pResult || {};
		tmpState.LastResult = { Message: tmpResult.Message || 'Import complete.', Entity: pEntityName };
		tmpState.LastError = '';
		if (this.modal && (typeof this.modal.toast === 'function'))
		{
			this.modal.toast(tmpResult.Message || 'Import complete.', { type: 'success' });
		}
		this._render();
	}

	/** @param {Error|string} pError */
	_onError(pError)
	{
		const tmpState = this._ensureState();
		const tmpMessage = (pError && pError.message) ? pError.message : String(pError || 'Import failed.');
		tmpState.LastError = tmpMessage;
		if (this.modal && (typeof this.modal.toast === 'function'))
		{
			this.modal.toast(tmpMessage, { type: 'error', duration: 6000 });
		}
		this._render();
	}

	/** Re-render the host view (re-derives slots + re-mounts the wizard via its onAfterRender). */
	_render()
	{
		if (this.pict.views['DataImport'])
		{
			this.pict.views['DataImport'].render();
		}
	}
}

module.exports = DataImportManagerProvider;
module.exports.default_configuration = _DEFAULT_CONFIGURATION;
